import {Injectable} from '@angular/core';
import {ClientEvents} from 'shared/client-events';
import {environment} from 'src/environments/environment';
import {isInBrowser} from '../utils/clientutils';

const VISITOR_ID_KEY = 'vid';
const FLUSH_DELAY_MS = 5000;
const MAX_QUEUE = 100;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Клиентские события аналитики (docs/specs/client-events.md §8).
 * Копит события в памяти и отправляет HTTP-пачками (sendBeacon, запасной путь — fetch keepalive).
 * Fire-and-forget: повторов нет, исключения наружу не выходят, вне браузера (SSG) — no-op.
 */
@Injectable({
    providedIn: 'root'
})
export class ClientEventsService {
    private queue: ClientEvents.Event[] = [];
    private timer: ReturnType<typeof setTimeout> | null = null;
    private listenersAttached = false;
    private visitorId: string | null = null;

    track(event: string, props?: Record<string, string | number | boolean>): void {
        if (!isInBrowser()) return;
        try {
            this.attachListeners();
            const item: ClientEvents.Event = {e: event, ts: Date.now(), page: location.pathname};
            const p = this.cleanProps(props);
            if (p) item.p = p;
            this.queue.push(item);
            if (this.queue.length > MAX_QUEUE) {
                this.queue.splice(0, this.queue.length - MAX_QUEUE);
            }
            if (this.queue.length >= ClientEvents.MAX_EVENTS_PER_BATCH) {
                this.flush();
            } else {
                this.scheduleFlush();
            }
        } catch {
            // аналитика не должна ломать приложение
        }
    }

    private cleanProps(props?: Record<string, string | number | boolean>): Record<string, string | number | boolean> | undefined {
        if (!props) return undefined;
        const result: Record<string, string | number | boolean> = {};
        for (const key of Object.keys(props)) {
            if (props[key] !== undefined) result[key] = props[key];
        }
        return Object.keys(result).length ? result : undefined;
    }

    private attachListeners(): void {
        if (this.listenersAttached) return;
        this.listenersAttached = true;
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') this.flush();
        });
        window.addEventListener('pagehide', () => this.flush());
    }

    private scheduleFlush(): void {
        if (this.timer) return;
        this.timer = setTimeout(() => this.flush(), FLUSH_DELAY_MS);
    }

    private flush(): void {
        try {
            if (this.timer) {
                clearTimeout(this.timer);
                this.timer = null;
            }
            if (!this.queue.length) return;
            const events = this.queue;
            this.queue = [];
            const vid = this.getVisitorId();
            const s = this.readSession();
            for (let i = 0; i < events.length; i += ClientEvents.MAX_EVENTS_PER_BATCH) {
                const batch: ClientEvents.Batch = {
                    v: 1,
                    vid,
                    events: events.slice(i, i + ClientEvents.MAX_EVENTS_PER_BATCH)
                };
                if (s) batch.s = s;
                this.send(JSON.stringify(batch));
            }
        } catch {
            // аналитика не должна ломать приложение
        }
    }

    private send(json: string): void {
        const url = environment.api + ClientEvents.BATCH_PATH;
        try {
            if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function'
                && navigator.sendBeacon(url, new Blob([json], {type: 'text/plain'}))) {
                return;
            }
        } catch {
            // уйдём через fetch
        }
        try {
            fetch(url, {
                method: 'POST',
                body: json,
                keepalive: true,
                headers: {'Content-Type': 'text/plain'}
            }).catch(() => {
            });
        } catch {
            // событие потеряно
        }
    }

    private readSession(): string | undefined {
        try {
            return window.sessionStorage.getItem('session') || undefined;
        } catch {
            return undefined;
        }
    }

    /**
     * Читает localStorage при каждом вызове: две вкладки первого визита сходятся на одном id.
     * Значение в памяти — только запасной вариант для недоступного storage.
     */
    getVisitorId(): string {
        if (!isInBrowser()) return '';
        try {
            const stored = window.localStorage.getItem(VISITOR_ID_KEY);
            if (stored && UUID_RE.test(stored)) {
                this.visitorId = stored;
                return stored;
            }
        } catch {
            // storage недоступен — используем UUID в памяти
        }
        if (!this.visitorId) this.visitorId = this.generateUuid();
        try {
            window.localStorage.setItem(VISITOR_ID_KEY, this.visitorId);
        } catch {
            // остаётся в памяти
        }
        return this.visitorId;
    }

    private generateUuid(): string {
        if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
        const b = crypto.getRandomValues(new Uint8Array(16));
        b[6] = (b[6] & 0x0f) | 0x40;
        b[8] = (b[8] & 0x3f) | 0x80;
        const h = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
        return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
    }
}
